<template>
    <el-dialog
        title="历史采集数据"
        :model-value="modelValue"
        top="10vh"
        width="90vw"
        @open="dialogOpen"
        @close="closed"
        :close-on-click-modal="false"
    >
        <div class="tool-box">
            <el-button type="primary" :icon="Refresh" @click="resetQueryForm">刷新</el-button>
            <el-button @click="handleExport" :icon="Upload">详细数据导出</el-button>
        </div>
        <div class="tool-box">
            <div class="search-box">
                <el-date-picker
                    v-model="queryForms.date_range"
                    type="datetimerange"
                    range-separator="至"
                    start-placeholder="开始日期"
                    end-placeholder="结束日期"
                    value-format="YYYY-MM-DD HH:mm:ss"
                    style="margin-right: 10px"
                    :default-time="[new Date(2000, 1, 1, 0, 0, 0), new Date(2000, 2, 1, 23, 59, 59)]"
                />
                <el-button type="primary" :icon="Search" plain @click="getRecordData">搜索</el-button>
                <el-button :icon="Refresh" @click="resetQueryForm">重置</el-button>
            </div>
        </div>
        <el-table :data="table_data" border v-loading="tableLoading" stripe style="height: 55vh">
            <el-table-column label="上传时间" prop="update_time" min-width="160" align="center"></el-table-column>
            <el-table-column label="名称" prop="name" min-width="160" align="center">
                <template #default>{{ props.deviceDetail?.name }}</template>
            </el-table-column>
            <el-table-column label="ICCID" min-width="200" align="center">
                <template #default>{{ props.deviceDetail?.ICCID }}</template>
            </el-table-column>
            <el-table-column label="IMEI" min-width="150" align="center">
                <template #default>{{ props.deviceDetail?.IMEI }}</template>
            </el-table-column>
            <el-table-column label="运行状态" width="100px" prop="run_status" align="center">
                <template #default="{ row }">{{ row.run_status === 1 ? '正常' : '故障' }}</template>
            </el-table-column>
            <el-table-column label="信号强度" prop="signal">
                <template #default="{ row }">
                    <div class="signal-box">
                        <span class="num">{{ row.signal }}</span>
                        <div class="signal">
                            <span
                                v-for="num of 5"
                                class="bar"
                                :key="num"
                                :class="['bar' + num, num <= Math.floor(row.signal / 6) ? 'active' : '']"
                            ></span>
                        </div>
                    </div>
                </template>
            </el-table-column>
            <el-table-column label="开关状态" width="100px" prop="switch" align="center">
                <template #default="{ row }">{{ row.switch === 1 ? '开' : '关' }}</template>
            </el-table-column>
            <el-table-column label="运行模式" width="100px" prop="run_mode" align="center">
                <template #default="{ row }">{{ run_mode.find((item) => item.value === row.run_mode)?.label || '--' }}</template>
            </el-table-column>
            <el-table-column label="风速状态" width="100px" prop="set_wind_speed" align="center">
                <template #default="{ row }">
                    {{ speed.find((item) => item.value === row.set_wind_speed)?.label || '--' }}
                </template>
            </el-table-column>
            <el-table-column label="实时温度" width="100px" prop="temp" align="center">
                <template #default="{ row }">{{ row.temp ?? '--' }}℃</template>
            </el-table-column>
            <el-table-column label="设置温度" width="100px" prop="set_temp" align="center">
                <template #default="{ row }">{{ row.set_temp ?? '--' }}℃</template>
            </el-table-column>
            <el-table-column label="各时段高速风累计时长（分钟）" width="140" prop="high_time" align="center">
                <template #default="{ row }">{{ row.high_time }}</template>
            </el-table-column>
            <el-table-column label="各时段中速风累计时长（分钟）" width="140" prop="mid_time" align="center">
                <template #default="{ row }">{{ row.mid_time }}</template>
            </el-table-column>
            <el-table-column label="各时段低速风累计时长（分钟）" width="140" prop="low_time" align="center">
                <template #default="{ row }">{{ row.low_time }}</template>
            </el-table-column>
            <el-table-column label="时间区间" width="140" prop="time_period" align="center">
                <template #default="{ row }">{{ getTimeByPeriod(row.time_period) }}</template>
            </el-table-column>
        </el-table>
        <el-pagination
            v-if="table_data.length > 0"
            class="elPagination"
            @size-change="handleSizeChange"
            @current-change="handleCurrentChange"
            :current-page="queryForms.page"
            :page-size="queryForms.page_size"
            :page-sizes="[50, 100]"
            layout="total, sizes, prev, pager, next, jumper"
            :total="total"
            background
        />
    </el-dialog>
</template>
<script setup>
import { defineEmits, defineProps, ref, reactive } from 'vue'
import { getReportData } from '@/api/thermostat/devicePoint.js'
import { Refresh, Search, Upload } from '@element-plus/icons'
import Exceljs from 'exceljs'
import FileSaver from 'file-saver'
import { ElLoading, ElMessage, ElMessageBox } from 'element-plus'
import { lockOptions, speed, run_mode } from '@/constant/index.js'

const props = defineProps({
    modelValue: {
        type: Boolean,
        required: true
    },
    IMEI: {
        type: String,
        required: true
    },
    deviceDetail: {
        type: Object,
        default: () => ({})
    }
})
const emits = defineEmits(['update:modelValue', 'updateForm'])
// 弹窗打开
function dialogOpen() {
    getRecordData()
}

function getTimeByPeriod(period) {
    const key = `tStage${period}`
    return props.deviceDetail.hasOwnProperty(key) ? props.deviceDetail[key] : '--'
}
// 表格数据
const table_data = ref([])
const tableLoading = ref(false)
function getRecordData() {
    tableLoading.value = true
    const params = { ...queryForms.value }
    params.id = props.deviceDetail.id
    if (params.date_range) {
        params.start_time = params.date_range[0]
        params.end_time = params.date_range[1]
    }
    delete params.date_range
    getReportData(params).then((data) => {
        if (data.lists.length > 0) {
            table_data.value = data.lists
            total.value = data.total
        } else {
            table_data.value = []
            total.value = 0
        }
        tableLoading.value = false
    })
}

const queryForms = ref({
    date_range: null,
    page: 1,
    page_size: 50
})
const total = ref(0)
const handleSizeChange = (currentSize) => {
    queryForms.value.page_size = currentSize
    getRecordData()
}
const handleCurrentChange = (currentPage) => {
    queryForms.value.page = currentPage
    getRecordData()
}

// 重置
const resetQueryForm = () => {
    queryForms.value = {
        date_range: null,
        page: 1,
        page_size: 50
    }
    total.value = 0
    getRecordData()
}

// 详细数据导出
function handleExport() {
    ElMessageBox.confirm('确定导出当前日期范围的设备状态数据？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
    })
        .then(async () => {
            const loading = ElLoading.service({
                lock: true,
                text: '正在导出...',
                spinner: 'Loading',
                background: 'rgba(0, 0, 0, 0.7)'
            })

            let list = []
            const params = {
                page: 1,
                page_size: 10000,
                id: props.deviceDetail.id
            }
            if (queryForms.value.date_range) {
                params.start_time = queryForms.value.date_range[0]
                params.end_time = queryForms.value.date_range[1]
            }
            const { lists } = await getReportData(params)
            list = lists
            if (list.length <= 0) {
                loading.close()
                return ElMessage.error('没有数据可导出')
            }

            const workbook = new Exceljs.Workbook()
            workbook.created = new Date()
            workbook.modified = new Date()
            const worksheet = workbook.addWorksheet('设备状态数据', {
                properties: { defaultRowHeight: 22 },
                pageSetup: { horizontalCentered: true }
            })

            // 严格按照表格列顺序定义导出字段，无任何多余字段
            worksheet.columns = [
                { header: '上传时间', key: 'update_time', width: 22 },
                { header: '名称', key: 'name', width: 20 },
                { header: 'IMEI', key: 'IMEI', width: 20 },
                { header: 'ICCID', key: 'ICCID', width: 23 },
                { header: '运行状态', key: 'run_status', width: 12 },
                { header: '信号强度', key: 'signal', width: 15 },
                { header: '开关状态', key: 'switch', width: 12 },
                { header: '运行模式', key: 'run_mode', width: 12 },
                { header: '风速状态', key: 'wind_speed', width: 12 },
                { header: '实时温度', key: 'temp', width: 15 },
                { header: '设定温度', key: 'set_temp', width: 15 },
                { header: '各时段高速风累计时长（分钟）', key: 'high_time', width: 30 },
                { header: '各时段中速风累计时长（分钟）', key: 'mid_time', width: 30 },
                { header: '各时段低速风累计时长（分钟）', key: 'low_time', width: 30 },
                { header: '时间区间', key: 'time_period', width: 18 }
            ]

            // 数据映射完全匹配表格显示逻辑
            worksheet.addRows(
                list.map((row) => {
                    return {
                        IMEI: props.deviceDetail.IMEI,
                        name: props.deviceDetail.name,
                        ICCID: props.deviceDetail.ICCID,
                        update_time: row.update_time || '',
                        signal: row.signal || '',
                        temp: row.temp ?? '',
                        set_temp: row.set_temp ?? '',
                        humidity: row.humidity ?? '',
                        run_status: row.run_status === 1 ? '正常' : '故障',
                        lock: lockOptions.find((item) => item.value === row.lock)?.label || '',
                        switch: row.switch === 1 ? '开' : '关',
                        wind_speed: speed.find((item) => item.value === row.set_wind_speed)?.label || '',
                        run_mode: run_mode.find((item) => item.value === row.run_mode)?.label || '',
                        low_time: row.low_time || '0',
                        mid_time: row.mid_time || '0',
                        high_time: row.high_time || '0',
                        switch_timestamp: row.switch_timestamp || '',
                        time_period: getTimeByPeriod(row.time_period)
                    }
                })
            )

            // 表头样式优化
            const headerRow = worksheet.getRow(1)
            headerRow.alignment = { horizontal: 'center', vertical: 'middle' }
            headerRow.font = { bold: true }

            // 导出文件
            await workbook.xlsx.writeBuffer().then((buffer) => {
                const date = new Date()
                const [year, month, day, hour, minutes, seconds] = [
                    date.getFullYear(),
                    String(date.getMonth() + 1).padStart(2, '0'),
                    String(date.getDate()).padStart(2, '0'),
                    String(date.getHours()).padStart(2, '0'),
                    String(date.getMinutes()).padStart(2, '0'),
                    String(date.getSeconds()).padStart(2, '0')
                ]
                const fileName = `温控器设备状态数据_${year}${month}${day}_${hour}${minutes}${seconds}.xlsx`
                FileSaver.saveAs(new Blob([buffer]), fileName)
            })

            loading.close()
            ElMessage.success('导出成功！')
        })
        .catch(() => {})
}

// 关闭
function closed() {
    table_data.value = []
    queryForms.value = {
        date_range: null,
        page: 1,
        page_size: 50
    }
    emits('update:modelValue', false)
}
</script>

<style scoped>
.tool-box {
    display: flex;
}
.tool-box .search-box {
    display: flex;
    align-items: center;
}
</style>
